import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, RefreshCw, Send, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OperatorHint, usePublicSettings } from "@/components/DepositDialog";
import { useWallet } from "@/components/BalanceChip";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { operatorOf, useMomoPhone } from "@/lib/operators";

const inputCls = "h-12 rounded-xl border-white/10 bg-white/5 font-mono text-base";

const WithdrawForm = ({ settings, onCreated }) => {
  const { t, tErr, lang } = useI18n();
  const { user, setUser } = useAuth();
  const { data: wallet } = useWallet();
  const [saved, remember] = useMomoPhone(user, setUser);
  const [phone, setPhone] = useState(saved);
  const detected = operatorOf(phone);
  const operator = detected === "ORANGE" ? "ORANGE" : "MTN";
  const [amount, setAmount] = useState(String(settings.withdraw_min));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/withdraw", { amount: parseInt(amount, 10), phone, operator });
      remember(data.phone);
      onCreated(data);
    } catch (err) {
      toast.error(tErr(errCode(err)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="withdraw-form">
      <p className="text-sm text-slate-400">{t("wallet.available")} : <span className="font-mono text-emerald-400">{formatXAF(wallet?.available ?? 0, lang)}</span></p>
      {wallet?.to_wager > 0 && (
        <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100" data-testid="withdraw-wager-note">
          {t("wallet.withdrawable")} : <b className="font-mono">{formatXAF(wallet.withdrawable, lang)}</b>. {t("wallet.toWager", { a: formatXAF(wallet.to_wager, lang) })}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        {["MTN", "ORANGE"].map((o) => (
          <div key={o} data-testid={`withdraw-operator-${o.toLowerCase()}`} data-active={detected === o}
            className={`flex h-14 items-center justify-center rounded-xl border text-sm font-semibold transition-colors ${detected === o ? "border-amber-400 bg-white/10" : "border-white/10 bg-white/5 text-slate-500"}`}>
            {o === "MTN" ? "MTN MoMo" : "Orange Money"}
          </div>
        ))}
      </div>
      <div className="space-y-2"><Label className="text-slate-300">{t("deposit.phone")}</Label>
        <Input type="tel" inputMode="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="6XXXXXXXX" className={inputCls} data-testid="withdraw-phone-input" />
        <OperatorHint detected={detected} testId="withdraw-operator-hint" /></div>
      <div className="space-y-2"><Label className="text-slate-300">{t("deposit.amount")}</Label>
        <Input type="number" inputMode="numeric" required min={settings.withdraw_min} max={settings.withdraw_max} step={1} value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} data-testid="withdraw-amount-input" />
        <p className="text-xs text-slate-500">{t("deposit.range", { min: settings.withdraw_min, max: settings.withdraw_max })}</p></div>
      <button type="submit" disabled={busy || !detected || detected === "unknown"} data-testid="withdraw-submit-btn"
        className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-display font-bold text-[#0B0D12] transition-transform active:scale-[0.98] disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} {t("withdraw.submit")}
      </button>
    </form>
  );
};

const ICON = { SUCCESSFUL: [CheckCircle2, "text-emerald-400"], FAILED: [XCircle, "text-red-400"], AWAITING_APPROVAL: [Clock, "text-amber-300"], REVIEW: [Clock, "text-amber-300"] };

const WithdrawStatus = ({ p, setP, onClose }) => {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const pending = ["PENDING", "INITIATING"].includes(p.status);
  const verify = async () => {
    setBusy(true);
    const { data } = await api.post(`/payments/${p.id}/verify`).catch(() => ({ data: p }));
    setP(data);
    qc.invalidateQueries({ queryKey: ["wallet"] });
    qc.invalidateQueries({ queryKey: ["payments"] });
    qc.invalidateQueries({ queryKey: ["ledger"] });
    setBusy(false);
  };
  useEffect(() => {
    if (!pending) return undefined;
    const id = setInterval(verify, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, p.id]);
  const [Icon, cls] = ICON[p.status] || [Loader2, "animate-spin text-amber-400"];
  return (
    <div className="space-y-4 text-center" data-testid={`withdraw-status-${p.status.toLowerCase()}`}>
      <Icon className={`mx-auto h-14 w-14 ${cls}`} />
      <h3 className="font-display text-xl font-bold">{t(`withdraw.s_${p.status}`)}</h3>
      <p className="font-mono text-2xl text-amber-300">{formatXAF(p.amount, lang)}</p>
      <p className="text-sm text-slate-400">{p.operator} · <span className="font-mono">{p.phone}</span></p>
      {pending && (
        <button onClick={verify} disabled={busy} data-testid="withdraw-verify-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-amber-500/50 font-semibold text-amber-300">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("deposit.verify")}
        </button>
      )}
      <button onClick={onClose} className="h-12 w-full rounded-full bg-amber-500 font-semibold text-[#0B0D12]" data-testid="withdraw-close-btn">{t("deposit.close")}</button>
    </div>
  );
};

export const WithdrawDialog = ({ open, onOpenChange }) => {
  const { t } = useI18n();
  const { data: settings } = usePublicSettings();
  const [p, setP] = useState(null);
  useEffect(() => {
    if (open) setP(null);
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl border-white/10 bg-[#121620] sm:max-w-md" data-testid="withdraw-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{t("withdraw.title")}</DialogTitle></DialogHeader>
        {settings && (p ? <WithdrawStatus p={p} setP={setP} onClose={() => onOpenChange(false)} /> : <WithdrawForm settings={settings} onCreated={setP} />)}
      </DialogContent>
    </Dialog>
  );
};
